import React, { useCallback, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
} from 'antd';
import {
  ArrowLeft,
  Users,
  Activity,
  Copy,
  RefreshCw,
  Trash2,
  UserPlus,
  BookOpen,
  ExternalLink,
  ArrowRightLeft
} from 'lucide-react';
import { ChartIcon } from './icons/SketchIcons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import InternalLayout from './InternalLayout';
import './ClassDetails.css';

const ClassDetails = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState('');
  const [transferMessage, setTransferMessage] = useState('');

  const canTransfer = user?.role === 'org_admin' || user?.role === 'platform_admin';

  const fetchClassDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/classes/${classId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setClassData(response.data);
      if (canTransfer) {
        setTeacherId(String(response.data.teacher_id || ''));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching class details:', error);
      setLoading(false);
    }
  }, [canTransfer, classId]);

  const fetchTeachers = async () => {
    setTransferMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const all = response.data || [];
      const teacherRows = all
        .filter((u) => u.role === 'teacher' && u.is_active)
        .sort((a, b) => String(a.full_name || a.email || '').localeCompare(String(b.full_name || b.email || '')));
      setTeachers(teacherRows);
    } catch (error) {
      setTransferMessage(error.response?.data?.detail || 'Failed to load teachers.');
    }
  };

  useEffect(() => {
    if (!canTransfer || !showTransfer) return;
    fetchTeachers();
  }, [canTransfer, showTransfer]);

  const submitTransfer = async () => {
    if (!teacherId) {
      setTransferMessage('Select a teacher.');
      return;
    }
    setTransferMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        `/api/classes/${classId}/teacher`,
        { teacher_id: Number(teacherId) },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setClassData((prev) => ({
        ...prev,
        teacher_id: response.data.teacher_id,
        teacher_name: response.data.teacher_name,
        teacher_email: response.data.teacher_email
      }));
      setShowTransfer(false);
    } catch (error) {
      setTransferMessage(error.response?.data?.detail || 'Failed to transfer class.');
    }
  };

  const fetchStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/classes/${classId}/students`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, [classId]);

  useEffect(() => {
    fetchClassDetails();
    fetchStudents();
  }, [fetchClassDetails, fetchStudents]);

  const handleCopyJoinCode = () => {
    navigator.clipboard.writeText(classData.join_code);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleRegenerateCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `/api/classes/${classId}/regenerate-code`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setClassData(response.data);
      alert('Join code regenerated successfully!');
    } catch (error) {
      console.error('Error regenerating join code:', error);
      alert('Failed to regenerate join code');
    }
  };

  const handleDeleteClass = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `/api/classes/${classId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Class deleted successfully');
      navigate('/teaching');
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class');
    }
  };

  if (loading) {
    return (
      <div className="class-details-container">
        <div className="loading">Loading class details...</div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="class-details-container">
        <div className="error-state">Class not found</div>
      </div>
    );
  }

  return (
    <InternalLayout
      title={classData.name}
      description={classData.description || (classData.teacher_name ? `Teacher: ${classData.teacher_name}` : 'Class details and activity')}
      actions={(
        <Space wrap>
          <Link to="/teaching">
            <Button icon={<ArrowLeft size={18} />}>Back to Dashboard</Button>
          </Link>
          <Popconfirm
            title="Delete this class?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={handleDeleteClass}
          >
            <Button danger icon={<Trash2 size={18} />} title="Delete Class" />
          </Popconfirm>
          {canTransfer && (
            <Button icon={<ArrowRightLeft size={18} />} onClick={() => setShowTransfer(true)} title="Transfer Class">
              Transfer
            </Button>
          )}
        </Space>
      )}
    >
    <div className="class-details-container">
      {showTransfer && (
        <Modal open onCancel={() => setShowTransfer(false)} footer={null} centered title="Transfer Class">
          <div className="transfer-modal">
            <p>Select a teacher in this organization.</p>
            {transferMessage && <Alert className="transfer-message" type="error" showIcon message={transferMessage} />}
            <label>
              Teacher
              <Select
                value={teacherId}
                onChange={setTeacherId}
                options={[
                  { value: '', label: 'Select teacher' },
                  ...teachers.map((t) => ({ value: String(t.id), label: `${t.full_name || t.email} (#${t.id})` })),
                ]}
              />
            </label>
            <div className="transfer-actions">
              <Button onClick={() => setShowTransfer(false)}>
                Cancel
              </Button>
              <Popconfirm
                title="Transfer this class?"
                description="The selected teacher will become the owner."
                okText="Transfer"
                onConfirm={submitTransfer}
              >
                <Button type="primary">Transfer</Button>
              </Popconfirm>
            </div>
          </div>
        </Modal>
      )}

      {/* Stats Overview */}
      <Row className="class-stats-overview" gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-box">
            <Statistic title="Total Participants" value={classData.student_count + classData.guest_count} prefix={<Users size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-box">
            <Statistic title="Registered Students" value={classData.student_count} prefix={<UserPlus size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-box">
            <Statistic title="Total Sessions" value={classData.total_sessions} prefix={<Activity size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-box">
            <Statistic title="Assigned Modules" value={classData.module_count || 0} prefix={<BookOpen size={20} />} />
          </Card>
        </Col>
      </Row>

      {/* Join Code Card */}
      <Card className="join-code-card" title="Class Join Code">
        <p>Share this code with your students to join the class</p>
        
        <div className="join-code-display">
          <code className="join-code-large">{classData.join_code}</code>
          <div className="join-code-actions">
            <Button
              icon={<Copy size={18} />}
              onClick={handleCopyJoinCode}
            >
              {copySuccess ? 'Copied!' : 'Copy Code'}
            </Button>
            <Popconfirm
              title="Regenerate the join code?"
              description="The old code will no longer work."
              okText="Regenerate"
              onConfirm={handleRegenerateCode}
            >
              <Button icon={<RefreshCw size={18} />}>Regenerate</Button>
            </Popconfirm>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="tabs-container">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'students', label: <span><Users size={18} /> Students ({students.length})</span> },
            { key: 'modules', label: <span><BookOpen size={18} /> Modules ({classData.module_count || 0})</span> },
            { key: 'analytics', label: <span><Activity size={18} /> Analytics</span> },
          ]}
        />

        <div className="tab-content">
          {activeTab === 'students' && (
            <StudentsTab classId={classId} students={students} onRefresh={fetchStudents} />
          )}
          {activeTab === 'modules' && (
            <ModulesTab classId={classId} joinCode={classData.join_code} totalStudents={students.length} />
          )}
          {activeTab === 'analytics' && (
            <AnalyticsTab classId={classId} students={students} />
          )}
        </div>
      </div>
    </div>
    </InternalLayout>
  );
};

const StudentsTab = ({ classId, students, onRefresh }) => {
  const { user } = useAuth();
  const canManage = user?.role === 'teacher' || user?.role === 'org_admin' || user?.role === 'platform_admin';
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');

  const addStudent = async () => {
    if (!email.trim()) {
      setMsg('Enter a student email.');
      return;
    }
    setAdding(true);
    setMsg('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `/api/classes/${classId}/students`,
        { email: email.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEmail('');
      onRefresh();
    } catch (error) {
      setMsg(error.response?.data?.detail || 'Failed to add student.');
    } finally {
      setAdding(false);
    }
  };

  const removeStudent = async (student) => {
    setMsg('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `/api/classes/${classId}/students/${student.user_id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      onRefresh();
    } catch (error) {
      setMsg(error.response?.data?.detail || 'Failed to remove student.');
    }
  };

  if (students.length === 0) {
    return (
      <div className="empty-tab-state">
        <Users size={64} color="#ccc" />
        <h3>No students yet</h3>
        <p>Students will appear here once they join using the class code</p>
        {canManage && (
          <div className="student-manage">
            <h4>Add Student</h4>
            <div className="student-manage-row">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="student@email.com"
              />
              <Button type="primary" onClick={addStudent} loading={adding}>
                {adding ? 'Adding...' : 'Add'}
              </Button>
            </div>
            {msg && <Alert className="student-manage-msg" type="error" showIcon message={msg} />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="students-list">
      {canManage && (
        <div className="student-manage">
          <h4>Add Student</h4>
          <div className="student-manage-row">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="student@email.com"
            />
            <Button type="primary" onClick={addStudent} loading={adding}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </div>
          {msg && <Alert className="student-manage-msg" type="error" showIcon message={msg} />}
        </div>
      )}
      <Table
        className="students-table"
        rowKey={(student) => student.user_id || student.email || student.name}
        dataSource={students}
        pagination={false}
        scroll={{ x: 760 }}
        columns={[
          {
            title: 'Student',
            dataIndex: 'name',
            key: 'student',
            render: (_, student) => (
              <div className="student-info">
                <div className="student-avatar">
                  {student.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="student-name">{student.name}</div>
                  {student.email && (
                    <div className="student-email">{student.email}</div>
                  )}
                </div>
              </div>
            )
          },
          {
            title: 'Completed',
            dataIndex: 'completed_modules_count',
            key: 'completed',
            render: (value) => value || 0
          },
          {
            title: 'Sessions',
            dataIndex: 'total_sessions',
            key: 'sessions'
          },
          {
            title: 'Events',
            dataIndex: 'total_events',
            key: 'events'
          },
          {
            title: 'Last Active',
            dataIndex: 'last_active',
            key: 'lastActive',
            render: (value) => (value ? new Date(value).toLocaleDateString() : 'Never')
          },
          ...(canManage
            ? [{
                title: 'Actions',
                key: 'actions',
                align: 'right',
                render: (_, student) => (
                  <Popconfirm
                    title={`Remove ${student.name} from this class?`}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => removeStudent(student)}
                  >
                    <Button danger>Remove</Button>
                  </Popconfirm>
                )
              }]
            : [])
        ]}
      />
    </div>
  );
};

const ModulesTab = ({ classId, joinCode, totalStudents }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [expandedId, setExpandedId] = useState('');
  const [studentRows, setStudentRows] = useState({});

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/classes/${classId}/module-tasks`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { only_active: showActiveOnly ? true : undefined }
        }
      );
      setModules(response.data || []);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load class module tasks.');
    } finally {
      setLoading(false);
    }
  }, [classId, showActiveOnly]);

  useEffect(() => {
    if (!classId) return;
    loadTasks();
  }, [classId, loadTasks]);

  const copyModuleLink = async (moduleId) => {
    try {
      const url = `${window.location.origin}/game/${moduleId}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(moduleId);
      setTimeout(() => setCopiedId(''), 1800);
    } catch (error) {
      setMessage('Copy failed. Please copy the link manually.');
    }
  };

  const openModule = (moduleId) => {
    const url = `/game/${moduleId}`;
    window.open(url, '_blank');
  };

  const toggleActive = async (mod, nextActive) => {
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/classes/${classId}/module-tasks/${mod.module_id}`,
        { is_active: nextActive },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setModules((prev) => prev.map((m) => (m.module_id === mod.module_id ? { ...m, is_active: nextActive } : m)));
      if (showActiveOnly && !nextActive) {
        await loadTasks();
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update task status.');
    }
  };

  const loadStudentStatus = async (moduleId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/classes/${classId}/module-tasks/${moduleId}/students`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setStudentRows((prev) => ({ ...prev, [moduleId]: response.data || [] }));
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load student activity.');
    }
  };

  const toggleExpand = async (moduleId) => {
    const next = expandedId === moduleId ? '' : moduleId;
    setExpandedId(next);
    if (next && !studentRows[next]) {
      await loadStudentStatus(next);
    }
  };

  if (loading) {
    return <div className="empty-tab-state"><p>Loading modules...</p></div>;
  }

  return (
    <div className="modules-tab">
      <div className="modules-tab-head">
        <div>
          <h3>Class Tasks</h3>
          <p>Students should join this class first using code <strong>{joinCode}</strong>.</p>
        </div>
        <div className="modules-tab-controls">
          <Switch
            checked={showActiveOnly}
            checkedChildren="Active only"
            unCheckedChildren="All tasks"
            onChange={setShowActiveOnly}
          />
        </div>
      </div>

      {message && <Alert className="modules-message" type="error" showIcon message={message} />}

      {!modules.length ? (
        <div className="empty-tab-state">
          <Empty description="No modules found. Enable published modules for this organization first." />
        </div>
      ) : (
        <Row className="modules-grid" gutter={[16, 16]}>
          {modules.map((mod) => (
            <Col xs={24} lg={12} xl={8} key={mod.module_id}>
              <Card
                className="module-card"
                title={mod.title}
                extra={mod.subject ? <Tag>{mod.subject}</Tag> : null}
              >

              <div className="module-row">
                <Switch
                  checked={!!mod.is_active}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  onChange={(checked) => toggleActive(mod, checked)}
                />
                <div className="module-progress">
                  Played {mod.played_students} / {mod.total_students || totalStudents}
                </div>
              </div>

              <Space className="module-actions" wrap>
                <Button icon={<Copy size={16} />} onClick={() => copyModuleLink(mod.module_id)}>
                  {copiedId === mod.module_id ? 'Copied' : 'Copy Link'}
                </Button>
                <Button icon={<Users size={16} />} onClick={() => toggleExpand(mod.module_id)}>
                  {expandedId === mod.module_id ? 'Hide Students' : 'View Students'}
                </Button>
                <Button type="primary" icon={<ExternalLink size={16} />} onClick={() => openModule(mod.module_id)}>
                  Open
                </Button>
              </Space>

              {expandedId === mod.module_id && (
                <div className="module-students">
                  <div className="module-students-head">
                    <span>Student</span>
                    <span>Played</span>
                    <span>Completed</span>
                    <span>Sessions</span>
                    <span>Events</span>
                    <span>Last Active</span>
                  </div>
                  {(studentRows[mod.module_id] || []).map((s) => (
                    <div key={s.user_id} className="module-students-row">
                      <span>{s.name}</span>
                      <span>{s.played ? 'Yes' : 'No'}</span>
                      <span>{s.completed ? 'Yes' : 'No'}</span>
                      <span>{s.total_sessions}</span>
                      <span>{s.total_events}</span>
                      <span>{s.last_active ? new Date(s.last_active).toLocaleDateString() : '—'}</span>
                    </div>
                  ))}
                  {studentRows[mod.module_id] && studentRows[mod.module_id].length === 0 && (
                    <div className="module-students-empty">No students in this class yet.</div>
                  )}
                </div>
              )}
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

const AnalyticsTab = ({ classId, students }) => {
  const totalSessions = students.reduce((sum, s) => sum + s.total_sessions, 0);
  const totalEvents = students.reduce((sum, s) => sum + s.total_events, 0);
  const avgSessionsPerStudent = students.length > 0 
    ? (totalSessions / students.length).toFixed(1) 
    : 0;

  return (
    <div className="analytics-content">
      <h3>Class Analytics</h3>
      
      <Row className="analytics-grid" gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card className="analytics-card">
            <Statistic title="Total Sessions" value={totalSessions} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="analytics-card">
            <Statistic title="Total Events Collected" value={totalEvents} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="analytics-card">
            <Statistic title="Avg Sessions/Student" value={avgSessionsPerStudent} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="analytics-card">
            <Statistic title="Active Students" value={students.filter(s => s.last_active).length} />
          </Card>
        </Col>
      </Row>

      <div className="analytics-notice">
        <p className="notice-title">
          <ChartIcon size={18} className="li-ico" />
          <span>Detailed analytics dashboard with charts and engagement metrics
          will be available in a future update.</span>
        </p>
      </div>
    </div>
  );
};

export default ClassDetails;
